import { CFDFile } from '@aero-platform/shared'
import { UploadTypes } from './constants'

/**
 * Helpers for confirming that a per-case `.sim` template a user just uploaded has actually
 * landed in Azure Blob Storage BEFORE a run is submitted/resubmitted.
 *
 * Per-case sim templates are uploaded direct-to-blob into a permanent, run-independent folder:
 *   cfd-cases/<raceTeamId>/sim-templates/{trackingId}/{filename}
 * The browser returns the directory URL synchronously (before the transfer finishes), so the
 * UI used to let the user submit while the multi-GB upload was still in flight — the run then
 * referenced a blob that had not landed yet and StarCCM silently fell back to the default sim.
 * These helpers list the target folder via the backend /ListBlobs endpoint to prove the file
 * is present.
 */

const SIM_TEMPLATES_MARKER = '/sim-templates/'

/**
 * Extract the `{trackingId}` folder segment from a per-case sim template `path`
 * (a full blob directory URL ending with `…/sim-templates/{trackingId}/`). This is the
 * `folderPath` the backend /ListBlobs endpoint expects for CASE_SIM_TEMPLATE_FILE — the server
 * prepends `{raceTeamId}/sim-templates/` itself.
 *
 * @returns the trailing-slash-free folder segment, or null if the path is not a per-case
 *          sim-templates URL.
 */
export function extractSimTemplateFolder(path?: string | null): string | null {
  if (!path) return null
  const idx = path.indexOf(SIM_TEMPLATES_MARKER)
  if (idx === -1) return null
  const folder = path.substring(idx + SIM_TEMPLATES_MARKER.length).replace(/\/+$/, '')
  return folder.length > 0 ? folder : null
}

/**
 * True when the template is a per-case `.sim` upload that lives under `/sim-templates/`
 * (rootType 'override'). Only these can — and must — be verified before submit; methodology
 * defaults and run-level staging_overrides are handled elsewhere.
 */
export function isVerifiablePerCaseUpload(template?: CFDFile | null): boolean {
  return (
    !!template &&
    template.rootType === 'override' &&
    typeof template.path === 'string' &&
    template.path.includes(SIM_TEMPLATES_MARKER) &&
    !!template.file
  )
}

/**
 * Confirm the given per-case sim template's blob is actually present in Azure by listing its
 * `/sim-templates/{trackingId}/` folder and checking the filename is there.
 *
 * @param template    The per-case sim template (must have path + file).
 * @param listBlobs   A function that lists blob filenames in a folder for a given upload type
 *                    (bind to `listBlobsInFolder` from blobUploadService).
 * @returns true if the exact filename is present in the folder, false otherwise.
 */
export async function verifySimTemplateLanded(
  template: CFDFile | null | undefined,
  listBlobs: (uploadType: string, folderPath: string) => Promise<string[]>
): Promise<boolean> {
  if (!isVerifiablePerCaseUpload(template)) return false
  const folderPath = extractSimTemplateFolder(template!.path)
  if (!folderPath) return false
  const files = await listBlobs(UploadTypes.CASE_SIM_TEMPLATE_FILE, folderPath)
  const target = template!.file
  return files.some(f => f === target)
}
